import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { DateTime } from "luxon";
import { requireAuth } from "@/lib/auth";
import { AppLayout, PageTransition, useHeaderConfig } from "@/components/layout";
import { useAuth } from "@/components/providers/AuthProvider";
import { Icon } from "@/components/ui/Icon";
import { Skeleton } from "@/components/ui/Skeleton";
import {
  useFetchUserProfileQuery,
  useFetchUserDiscoverableClubsQuery,
  useCheckBlockStatusQuery,
  useBlockUserMutation,
  useUnblockUserMutation,
} from "@/api/clubsApi";
import type { UserProfile, Club } from "@/api/clubsApi";
import { userDisplayName } from "@tinyclub/shared/utils";

export const Route = createFileRoute("/profile/$userId")({
  beforeLoad: requireAuth,
  component: UserProfilePage,
});

function UserProfilePage() {
  const { userId } = Route.useParams();
  const { user } = useAuth();
  const currentUserId = user?.id;

  // Fetch profile with topics included
  const {
    data: profile,
    isLoading,
    error,
  } = useFetchUserProfileQuery({ id: userId, includeTopics: true });

  // Fetch discoverable clubs
  const { data: discoverableClubs } = useFetchUserDiscoverableClubsQuery(userId);

  // Set header configuration (same pattern as other pages)
  useHeaderConfig(
    {
      breadcrumbs: [{ label: "Profile" }],
    },
    []
  );

  return (
    <AppLayout>
      <PageTransition>
        {/* Content */}
        <div className="p-6 pb-24">
          <div className="max-w-lg mx-auto space-y-6">
            {/* Error state */}
            {error ? (
              <div className="text-center py-8">
                <p className="text-text-secondary">
                  Failed to load profile. Please try again.
                </p>
              </div>
            ) : isLoading ? (
              <ProfileSkeleton />
            ) : profile ? (
              <ProfileContent
                profile={profile}
                discoverableClubs={discoverableClubs}
                currentUserId={currentUserId}
              />
            ) : (
              <div className="text-center py-8">
                <p className="text-text-secondary">Profile not found</p>
              </div>
            )}
          </div>
        </div>
      </PageTransition>
    </AppLayout>
  );
}

function ProfileContent({
  profile,
  discoverableClubs,
  currentUserId,
}: {
  profile: UserProfile;
  discoverableClubs?: { clubs: Club }[];
  currentUserId?: string;
}) {
  const displayName = userDisplayName(profile);
  const isOwnProfile = currentUserId === profile.id;

  // Block status
  const { data: blockStatus } = useCheckBlockStatusQuery(
    { userId: currentUserId!, otherUserId: profile.id },
    { skip: !currentUserId || isOwnProfile }
  );
  const [blockUser, { isLoading: isBlocking }] = useBlockUserMutation();
  const [unblockUser, { isLoading: isUnblocking }] = useUnblockUserMutation();
  const [showBlockConfirm, setShowBlockConfirm] = useState(false);
  const [showUnblockConfirm, setShowUnblockConfirm] = useState(false);

  // Extract location from location_object
  const locationObject = profile.location_object as {
    properties?: {
      context?: {
        place?: { name?: string };
        region?: { name?: string };
      };
    };
  } | null;
  const locationCity = locationObject?.properties?.context?.place?.name;
  const locationState = locationObject?.properties?.context?.region?.name;
  const locationDisplay =
    locationCity && locationState ? `${locationCity}, ${locationState}` : profile.location_name;

  // Format birthdate
  const birthdayDisplay = formatBirthdate(
    profile.birthdate,
    profile.show_year_of_birth,
    profile.show_zodiac
  );

  // Format joined date
  const joinedDisplay = profile.created_at
    ? `Joined ${DateTime.fromISO(profile.created_at).toFormat("MMMM yyyy")}`
    : null;

  // Check if we have any bio details
  const hasBioDetails =
    locationDisplay ||
    profile.job_title ||
    birthdayDisplay ||
    profile.education_name ||
    joinedDisplay;

  // Check if we have social links
  const hasSocialLinks =
    profile.web_personal ||
    profile.instagram ||
    profile.tiktok ||
    profile.strava ||
    profile.linkedin;

  return (
    <div className="space-y-6">
      {/* Avatar */}
      <div className="flex justify-center">
        <div className="relative">
          <div className="w-48 h-48 rounded-3xl overflow-hidden shadow-lg border border-border-tertiary">
            {profile.avatar_full || profile.avatar_medium ? (
              <img
                src={profile.avatar_full || profile.avatar_medium || ""}
                alt={displayName}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-surface-1 flex items-center justify-center">
                <Icon name="user" size={64} className="text-text-tertiary" />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Name and username section */}
      <div className="text-center space-y-1">
        {/* Name and Pronouns */}
        <div className="flex items-baseline justify-center gap-2">
          <h1 className="text-h3 text-text-primary">{displayName}</h1>
          {profile.pronouns && (
            <span className="text-body-2 text-text-secondary">{profile.pronouns}</span>
          )}
        </div>

        {/* Username */}
        {profile.username && (
          <p className="text-body-1-medium text-text-primary">@{profile.username}</p>
        )}
      </div>

      {/* Bio */}
      {profile.bio && (
        <p className="text-body-2 text-text-primary text-center">{profile.bio}</p>
      )}

      {/* Bio details panel */}
      {hasBioDetails && (
        <div className="bg-surface-1 rounded-2xl p-4 space-y-4">
          {locationDisplay && (
            <ProfileDetailRow icon="house" text={`Lives in ${locationDisplay}`} />
          )}
          {profile.job_title && (
            <ProfileDetailRow icon="briefcase" text={profile.job_title} />
          )}
          {birthdayDisplay && (
            <ProfileDetailRow icon="cake" text={`Born on ${birthdayDisplay}`} />
          )}
          {profile.education_name && (
            <ProfileDetailRow icon="graduation-cap" text={profile.education_name} />
          )}
          {joinedDisplay && (
            <ProfileDetailRow icon="calendar" text={joinedDisplay} />
          )}
        </div>
      )}

      {/* Social links */}
      {hasSocialLinks && (
        <div className="flex flex-wrap gap-2">
          {profile.web_personal && (
            <SocialLinkPill
              icon="link"
              text={profile.web_personal}
              href={ensureHttps(profile.web_personal)}
            />
          )}
          {profile.instagram && (
            <SocialLinkPill
              icon="instagram"
              text={profile.instagram}
              href={`https://instagram.com/${profile.instagram}`}
            />
          )}
          {profile.tiktok && (
            <SocialLinkPill
              icon="tiktok"
              text={profile.tiktok}
              href={`https://tiktok.com/@${profile.tiktok}`}
            />
          )}
          {profile.strava && (
            <SocialLinkPill
              icon="strava"
              text={profile.strava}
              href={`https://strava.com/athletes/${profile.strava}`}
            />
          )}
          {profile.linkedin && (
            <SocialLinkPill
              icon="linkedin"
              text={profile.linkedin}
              href={`https://linkedin.com/in/${profile.linkedin}`}
            />
          )}
        </div>
      )}

      {/* Interests section */}
      {profile.user_topics && profile.user_topics.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-h6 text-text-primary">Interests</h3>
          <div className="flex flex-wrap gap-2">
            {profile.user_topics.map((userTopic) => {
              const isSubtopic = !!userTopic.subtopic_id;
              const topicName = isSubtopic
                ? userTopic.subtopics?.name
                : userTopic.topics?.name;
              const emoji = isSubtopic
                ? userTopic.subtopics?.parent_topic?.emoji
                : userTopic.topics?.emoji;

              return (
                <div
                  key={userTopic.id}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-border-tertiary bg-surface"
                >
                  {emoji && <span className="text-body-2">{emoji}</span>}
                  <span className="text-body-2 text-text-primary">
                    {topicName}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Clubs section */}
      {discoverableClubs && discoverableClubs.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-h6 text-text-primary">Clubs</h3>
          <div className="space-y-2">
            {discoverableClubs.map((item) => (
              <CompactClubCard key={item.clubs.id} club={item.clubs} />
            ))}
          </div>
        </div>
      )}

      {/* Block/Unblock — only show on other users' profiles */}
      {!isOwnProfile && currentUserId && blockStatus && (
        <div className="pt-4 border-t border-border-tertiary">
          {blockStatus.iBlockedThem ? (
            !showUnblockConfirm ? (
              <button
                onClick={() => setShowUnblockConfirm(true)}
                className="w-full p-3 rounded-full text-body-2-medium text-text-secondary hover:bg-surface-1 transition-colors"
              >
                Unblock {displayName}
              </button>
            ) : (
              <div className="bg-surface rounded-2xl p-4 space-y-3">
                <p className="text-body-1-medium text-text-primary">
                  Unblock {displayName}?
                </p>
                <p className="text-body-2 text-text-secondary">
                  They will be able to see your content and activity.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowUnblockConfirm(false)}
                    className="flex-1 p-3 rounded-full border border-border-tertiary text-body-2-medium text-text-primary hover:bg-surface-1 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={async () => {
                      try {
                        await unblockUser({ blockerId: currentUserId, blockedId: profile.id }).unwrap();
                        setShowUnblockConfirm(false);
                      } catch (error) {
                        console.error("Failed to unblock user:", error);
                      }
                    }}
                    disabled={isUnblocking}
                    className="flex-1 p-3 rounded-full bg-olive text-white text-body-2-medium hover:opacity-90 transition-opacity disabled:opacity-50"
                  >
                    {isUnblocking ? "Unblocking..." : "Unblock"}
                  </button>
                </div>
              </div>
            )
          ) : (
            !showBlockConfirm ? (
              <button
                onClick={() => setShowBlockConfirm(true)}
                className="w-full p-3 rounded-full text-body-2-medium text-accent-negative hover:bg-surface-1 transition-colors"
              >
                Block {displayName}
              </button>
            ) : (
              <div className="bg-surface rounded-2xl p-4 space-y-3">
                <p className="text-body-1-medium text-text-primary">
                  Block {displayName}?
                </p>
                <div className="space-y-2 text-body-2 text-text-secondary">
                  <p>They won't be able to see your posts or activity.</p>
                  <p>You won't see their posts or activity either.</p>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowBlockConfirm(false)}
                    className="flex-1 p-3 rounded-full border border-border-tertiary text-body-2-medium text-text-primary hover:bg-surface-1 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={async () => {
                      try {
                        await blockUser({ blockerId: currentUserId, blockedId: profile.id }).unwrap();
                        setShowBlockConfirm(false);
                      } catch (error) {
                        console.error("Failed to block user:", error);
                      }
                    }}
                    disabled={isBlocking}
                    className="flex-1 p-3 rounded-full bg-accent-negative text-white text-body-2-medium hover:opacity-90 transition-opacity disabled:opacity-50"
                  >
                    {isBlocking ? "Blocking..." : "Block"}
                  </button>
                </div>
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
}

function ProfileDetailRow({ icon, text }: { icon: string; text: string }) {
  return (
    <div className="flex items-center gap-3">
      <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
        <Icon name={icon} size={18} className="text-text-primary" />
      </div>
      <span className="text-body-2 text-text-primary">{text}</span>
    </div>
  );
}

function SocialLinkPill({
  icon,
  text,
  href,
}: {
  icon: string;
  text: string;
  href: string;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-surface-1 hover:bg-surface-2 transition-colors"
    >
      <Icon name={icon} size={18} className="text-text-primary" />
      <span className="text-body-2 text-text-primary">{text}</span>
    </a>
  );
}

function ProfileSkeleton() {
  return (
    <div className="space-y-6">
      {/* Avatar skeleton */}
      <div className="flex justify-center">
        <Skeleton className="w-48 h-48 rounded-3xl" />
      </div>

      {/* Name skeleton */}
      <div className="text-center space-y-2">
        <Skeleton className="h-8 w-48 mx-auto" />
        <Skeleton className="h-5 w-24 mx-auto" />
      </div>

      {/* Bio skeleton */}
      <div className="space-y-2">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4 mx-auto" />
      </div>

      {/* Details panel skeleton */}
      <Skeleton className="h-48 w-full rounded-2xl" />
    </div>
  );
}

// Helper functions

function formatBirthdate(
  birthdate: string | null | undefined,
  showYear: boolean | null | undefined,
  showZodiac: boolean | null | undefined
): string | null {
  if (!birthdate) return null;

  try {
    const date = DateTime.fromISO(birthdate);
    if (!date.isValid) return null;

    let result = date.toFormat("MMMM d");

    if (showYear) {
      result = date.toFormat("MMMM d, yyyy");
    }

    if (showZodiac) {
      const zodiac = getZodiacSign(date.month, date.day);
      if (zodiac) {
        result += ` (${zodiac})`;
      }
    }

    return result;
  } catch {
    return null;
  }
}

function getZodiacSign(month: number, day: number): string | null {
  const signs = [
    { name: "Capricorn", endMonth: 1, endDay: 19 },
    { name: "Aquarius", endMonth: 2, endDay: 18 },
    { name: "Pisces", endMonth: 3, endDay: 20 },
    { name: "Aries", endMonth: 4, endDay: 19 },
    { name: "Taurus", endMonth: 5, endDay: 20 },
    { name: "Gemini", endMonth: 6, endDay: 20 },
    { name: "Cancer", endMonth: 7, endDay: 22 },
    { name: "Leo", endMonth: 8, endDay: 22 },
    { name: "Virgo", endMonth: 9, endDay: 22 },
    { name: "Libra", endMonth: 10, endDay: 22 },
    { name: "Scorpio", endMonth: 11, endDay: 21 },
    { name: "Sagittarius", endMonth: 12, endDay: 21 },
    { name: "Capricorn", endMonth: 12, endDay: 31 },
  ];

  for (const sign of signs) {
    if (month < sign.endMonth || (month === sign.endMonth && day <= sign.endDay)) {
      return sign.name;
    }
  }

  return "Capricorn";
}

function ensureHttps(url: string): string {
  if (url.startsWith("http://") || url.startsWith("https://")) {
    return url;
  }
  return `https://${url}`;
}

// Compact club card for displaying in profile
function CompactClubCard({ club }: { club: Club }) {
  // Extract location from location_object (same pattern as profile)
  const locationObject = club.location_object as {
    properties?: {
      context?: {
        place?: { name?: string };
        region?: { name?: string };
      };
    };
  } | null;
  const locationCity = locationObject?.properties?.context?.place?.name;
  const locationState = locationObject?.properties?.context?.region?.name;
  const locationDisplay =
    locationCity && locationState
      ? `${locationCity}, ${locationState}`
      : club.location_name;

  return (
    <Link
      to="/clubs/$identifier"
      params={{ identifier: club.id! }}
      className="flex items-center gap-3 p-3 rounded-2xl bg-surface hover:bg-surface-1 transition-colors"
    >
      {/* Club image */}
      <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0 border border-border-tertiary">
        {club.primary_image_medium ? (
          <img
            src={club.primary_image_medium}
            alt={club.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-surface-1 flex items-center justify-center">
            <Icon name="user-group" size={20} className="text-text-tertiary" />
          </div>
        )}
      </div>

      {/* Club info */}
      <div className="flex-1 min-w-0">
        <p className="text-body-2-medium text-text-primary truncate">
          {club.name}
        </p>
        {locationDisplay && (
          <p className="text-body-3 text-text-secondary truncate">
            {locationDisplay}
          </p>
        )}
      </div>

      {/* Arrow */}
      <Icon name="angle-right" size={16} className="text-text-tertiary flex-shrink-0" />
    </Link>
  );
}
